export type HierarchyLink = {
  parentId: string | null
  id: string
  type: NodeType
}

export type NodeType = 'organization' | 'team'

export type NodeNeighbors = {
  left: string | undefined
  right: string | undefined
}

const isHierarchyLink = (value: unknown): value is HierarchyLink => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    typeof value.id === 'string' &&
    'parentId' in value &&
    (value.parentId === null || typeof value.parentId === 'string') &&
    'type' in value &&
    (value.type === 'organization' || value.type === 'team')
  )
}

const initialize = (): HierarchyLink[] => {
  return [{ id: '0', parentId: null, type: 'organization' }]
}

const isEmpty = (value: HierarchyLink[]) => {
  return value.length === 0 || (value?.length == 1 && value[0]?.type === 'organization')
}

const isHierarchyLinkArray = (value: unknown): value is HierarchyLink[] => {
  if (!Array.isArray(value)) return false
  return value.every(isHierarchyLink)
}

const fromJSON = (json: string | undefined | null): HierarchyLink[] | null => {
  if (!json) return []

  try {
    const data = JSON.parse(json)
    return isHierarchyLinkArray(data) ? data : null
  } catch (e) {
    return null
  }
}

const identifiers = (type: NodeType, hierarchy: HierarchyLink[]): Set<string> => {
  return hierarchy.reduce((acc, link) => {
    if (link.type === type) {
      acc.add(link.id)
    }
    return acc
  }, new Set<string>())
}
// wip until we remove all ids string
const identifiersAsNumber = (type: NodeType, hierarchy: HierarchyLink[]): Set<number> => {
  return hierarchy.reduce((acc, link) => {
    if (link.type === type) {
      acc.add(Number(link.id))
    }
    return acc
  }, new Set<number>())
}

const toJSON = (hierarchy: HierarchyLink[]): string => {
  return JSON.stringify(hierarchy)
}

const addNode = (node: HierarchyLink, hierarchy: HierarchyLink[]): HierarchyLink[] => {
  if (identifiers(node.type, hierarchy).has(node.id)) return hierarchy
  return [...hierarchy, node]
}

const removeNode = (node: HierarchyLink, hierarchy: HierarchyLink[]): HierarchyLink[] => {
  return hierarchy.filter((hierarchyNink) => hierarchyNink.id !== node.id)
}

const getParentId = (nodeId: string, hierarchy: HierarchyLink[]) => {
  return hierarchy.find((link) => link.id === nodeId)?.parentId
}

const getNeighbors = (
  nodeId: string,
  hierarchy: HierarchyLink[],
  parentId: string | null = null
): NodeNeighbors => {
  const parent = parentId ? parentId : getParentId(nodeId, hierarchy)
  const siblings = hierarchy.filter((link) => link.parentId === parent)
  const nodeIndex = siblings.findIndex((link) => link.id === nodeId)

  return {
    left: siblings[nodeIndex - 1]?.id,
    right: siblings[nodeIndex + 1]?.id,
  }
}

const removeNodeAndLinkedNodes = (
  hierarchy: HierarchyLink[],
  nodeIdToRemove: number
): HierarchyLink[] => {
  const linkedNodes = findLinkedNodes(hierarchy, nodeIdToRemove)
  return hierarchy.filter((node) => !linkedNodes.has(Number(node.id)))
}

const findLinkedNodes = (hierarchy: HierarchyLink[], nodeId: number): Set<number> => {
  const linkedNodes = new Set<number>()
  const stack = [nodeId]

  while (stack.length) {
    const currentId = stack.pop()
    if (currentId) {
      linkedNodes.add(currentId)
    }

    for (const node of hierarchy) {
      if (Number(node.parentId) === currentId) {
        stack.push(Number(node.id))
      }
    }
  }

  return linkedNodes
}

export default {
  addNode,
  removeNode,
  toJSON,
  fromJSON,
  identifiers,
  identifiersAsNumber,
  initialize,
  isEmpty,
  getParentId,
  getNeighbors,
  removeNodeAndLinkedNodes,
}
